import React, { useState } from 'react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  InputAdornment,
  Box,
  Typography,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
  FormHelperText
} from '@mui/material';
import { useTags } from '../../hooks/useTags';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

// Constants for validation
const TAG_NAME_MAX_LENGTH = 100;
const TAG_DESCRIPTION_MAX_LENGTH = 300;

export const TagSelectionModal = ({
  open,
  onClose,
  onSelectTag,
  itemId,
}) => {
  const theme = useTheme();
  const { tags, isLoading, createTag } = useTags();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  const handleTagClick = (tag) => {
    onSelectTag(tag.tag_id);
    onClose();
  };

  const validateInputs = () => {
    let isValid = true;
    
    // Validate tag name
    if (!newTagName.trim()) {
      setNameError('Tag name is required');
      isValid = false;
    } else if (newTagName.length > TAG_NAME_MAX_LENGTH) {
      setNameError(`Tag name must be ${TAG_NAME_MAX_LENGTH} characters or less`);
      isValid = false;
    } else {
      setNameError('');
    }
    
    // Validate description
    if (newTagDescription.length > TAG_DESCRIPTION_MAX_LENGTH) {
      setDescriptionError(`Description must be ${TAG_DESCRIPTION_MAX_LENGTH} characters or less`);
      isValid = false;
    } else {
      setDescriptionError('');
    }
    
    return isValid;
  };

  const handleCreateTag = async () => {
    if (!validateInputs()) return;
    
    try {
      setIsCreating(true);
      const newTag = await createTag(newTagName.trim(), newTagDescription.trim());
      
      // Apply the tag immediately to the receipt item
      if (itemId && onSelectTag) {
        await onSelectTag(newTag.tag_id);
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to create tag:', error);
    } finally {
      setIsCreating(false);
      setNewTagName('');
      setNewTagDescription('');
      setShowCreateForm(false);
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setNewTagName(value);
    
    // Clear error if fixed or show error if too long
    if (!value.trim()) {
      setNameError('Tag name is required');
    } else if (value.length > TAG_NAME_MAX_LENGTH) {
      setNameError(`Tag name must be ${TAG_NAME_MAX_LENGTH} characters or less`);
    } else {
      setNameError('');
    }
  };
  
  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setNewTagDescription(value);
    
    // Show error if description is too long
    if (value.length > TAG_DESCRIPTION_MAX_LENGTH) {
      setDescriptionError(`Description must be ${TAG_DESCRIPTION_MAX_LENGTH} characters or less`);
    } else {
      setDescriptionError('');
    }
  };

  const filteredTags = tags.filter(tag => 
    tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (tag.description && tag.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: 'rgba(17, 24, 39, 0.8)', // Dark header
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 2,
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        color: 'white'
      }}>
        Tags
        <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, backgroundColor: theme.palette.background.paper }}>
        <Box p={2} sx={{ backgroundColor: 'rgba(17, 24, 39, 0.8)' }}>
          <TextField
            placeholder="Search tags..."
            variant="outlined"
            fullWidth
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
              sx: { 
                borderRadius: 1,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.15)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.25)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main
                },
                color: 'white'
              }
            }}
          />
        </Box>
        
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
          </Box>
        ) : (
          <>
            <List sx={{ pt: 0 }}>
              {filteredTags.map((tag) => (
                <ListItemButton
                  key={tag.tag_id}
                  onClick={() => handleTagClick(tag)}
                  sx={{
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15)
                    },
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {tag.is_system_tag ? (
                      <SettingsIcon fontSize="small" sx={{ color: theme.palette.grey[400] }} />
                    ) : (
                      <LocalOfferIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" fontWeight={500} color="white">{tag.name}</Typography>
                        {tag.is_system_tag && (
                          <Chip
                            label="System"
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: 10,
                              fontWeight: 500,
                              backgroundColor: alpha('#607d8b', 0.2),
                              color: '#90caf9',
                              borderColor: 'transparent'
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={tag.description}
                    secondaryTypographyProps={{
                      variant: 'body2',
                      color: 'rgba(255, 255, 255, 0.6)'
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
            
            {filteredTags.length === 0 && !showCreateForm && (
              <Box p={3} textAlign="center">
                <Typography color="rgba(255, 255, 255, 0.6)" gutterBottom>
                  No matching tags found
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setShowCreateForm(true)}
                  size="small"
                  sx={{ color: theme.palette.primary.main }}
                >
                  Create new tag
                </Button>
              </Box>
            )}
            
            <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
            
            {showCreateForm ? (
              <Box p={2} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
                <Typography variant="subtitle2" fontWeight={500} mb={1.5} color="white">
                  Create new tag
                </Typography>
                <Typography variant="subtitle2" fontWeight={500} mb={1.5} color="gray">
                  Feel free to include emojis in your tag names!
                </Typography>
                <TextField
                  label="Tag name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={newTagName}
                  onChange={handleNameChange}
                  error={!!nameError}
                  helperText={nameError}
                  FormHelperTextProps={{
                    sx: { color: theme.palette.error.main }
                  }}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.15)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.25)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-error fieldset': {
                        borderColor: theme.palette.error.main,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'white',
                    }
                  }}
                />
                <TextField
                  label="Description (optional)"
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  value={newTagDescription}
                  onChange={handleDescriptionChange}
                  error={!!descriptionError}
                  helperText={descriptionError}
                  FormHelperTextProps={{
                    sx: { color: theme.palette.error.main }
                  }}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.15)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.25)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-error fieldset': {
                        borderColor: theme.palette.error.main,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'white',
                    }
                  }}
                />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="rgba(255, 255, 255, 0.5)">
                    {newTagName.length}/{TAG_NAME_MAX_LENGTH} • {newTagDescription.length}/{TAG_DESCRIPTION_MAX_LENGTH}
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Button 
                      variant="text"
                      onClick={() => {
                        setShowCreateForm(false);
                        setNewTagName('');
                        setNewTagDescription('');
                        setNameError('');
                        setDescriptionError('');
                      }}
                      disabled={isCreating}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleCreateTag}
                      disabled={!newTagName.trim() || isCreating || !!nameError || !!descriptionError}
                      startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : null}
                      sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark
                        }
                      }}
                    >
                      Create
                    </Button>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box p={2} textAlign="center" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCreateForm(true)}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    color: theme.palette.primary.main,
                    borderRadius: 1,
                    py: 1,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1)
                    }
                  }}
                >
                  Create new tag
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}; 